import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { api } from "@/lib/api";

/* Contribution — exact port of the "YUO - Contribution (standalone)" mockup.
   Nocturne tokens: bg var(--yuo-bg) · ink #eef0f6 · titanium #a9b7ce/#dfe7f4 · lilac (SIMBIOS only) #9184d9/#cfc6f7 */

const MONO = "'JetBrains Mono', ui-monospace, monospace";
const ON = "radial-gradient(120% 200% at 30% 0%, rgba(169,183,206,0.30), rgba(169,183,206,0.09))";
const OFF = "var(--fg-05)";
const CHIPS = [25, 50, 100, 150, 200, 250, 500, 1000, "custom"];

const T = {
  en: {
    contribution: "Contribution", close: "Close",
    role: "Systems methodologist · Engineer of applied instruments",
    bio: "A path from the dramaturgy of action and the word on stage to the architecture of thinking and the making of independent digital environments.",
    mission: "Mission",
    missionText: "Bringing living professional experience together with the light, intelligent authoring instruments of a new era.",
    method: "Methodological contribution",
    methodText: "Preserving the Word, opening talent, and refusing templated, imposed algorithms.",
    safety: "Safety and sovereignty",
    safetyList: ["Authorial silence — drafts and trials stay untouched.", "100% authorship: every material you create is yours.", "Local-first processing, with no outward leaks."],
    once: "One-time contribution", monthly: "Monthly support", own: "Own amount", ownPh: "€ amount",
    secure: "Stripe · secure", perMonth: " / month", onceSfx: " once",
    applePay: "Pay with Apple\u00a0Pay", orCard: "or card",
    contribute: (v) => `Contribute €${v}`, support: (v) => `Support €${v} monthly`,
    processing: "Processing…", thanks: "Thank you · contribution received", thanksMonthly: "Thank you · support is active",
    simbiosTitle: "Simbios zone · from €500",
    simbiosP1: "A contribution from €500 opens the Simbios working zone: you receive the creator's direct working email and become a participant in the design of new instruments through the “Method of the third solution”.",
    simbiosP2: "This is direct collaboration with the author: you can propose what the instrument is missing for you, or describe a solution your professional work requires — a new function, a module, or a separate applied instrument. We take apart your real practical tasks and build the needed capabilities into the YUO ecosystem.",
    simbiosOn: "Zone active · working email included", simbiosOff: "Choose €500 or more to open the zone",
    simbiosMail: (m) => `Creator's working email: ${m}`,
    cardNumber: "Card number", exp: "MM / YY", cvc: "CVC",
  },
};

const label = { fontSize: 9.5, letterSpacing: "0.24em", textTransform: "uppercase" };
const Stone = ({ size, color, shade, glow, style }) => (
  <span style={{ flex: "none", width: size, height: size, borderRadius: "50%", background: `radial-gradient(38% 34% at 32% 26%, #ffffff 0%, ${color} 22%, ${shade} 62%, #05060c 100%)`, boxShadow: glow, ...style }} />
);

const ELEMENT_STYLE = {
  base: { fontFamily: MONO, fontSize: "12.5px", color: "var(--yuo-ink)", fontWeight: "300", "::placeholder": { color: "var(--fg-55)" }, iconColor: "var(--fg-55)" },
  invalid: { color: "var(--lit-coral-10)", iconColor: "var(--lit-coral-10)" },
};
const FONT_SRC = "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500&display=swap";

let _stripePromise = null;
function stripeFor(pk) {
  if (!_stripePromise && pk) _stripePromise = loadStripe(pk);
  return _stripePromise;
}

export default function ContributionModal({ open, onClose, lang = "en", returnFocusRef }) {
  const t = T.en;
  const [mode, setMode] = useState("once");
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [cfg, setCfg] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | processing | success | error
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [canApple, setCanApple] = useState(false);
  const cardRef = useRef(null);
  const stripeRef = useRef(null);
  const elsRef = useRef({});
  const prRef = useRef(null);
  const intentRef = useRef(null);

  const amountValue = useMemo(() => {
    if (amount === "custom") { const n = parseFloat(String(custom).replace(/[^\d.]/g, "")); return isNaN(n) ? null : n; }
    return amount;
  }, [amount, custom]);
  const validAmount = amountValue !== null && amountValue >= 5 && amountValue <= 10000 ? amountValue : null;
  const simbios = amountValue !== null && amountValue >= 500;
  const monthly = mode === "monthly";
  const hasAmount = amountValue !== null;
  const summary = hasAmount ? `€${amountValue}${monthly ? t.perMonth : t.onceSfx}` : "";
  const payLabel = !hasAmount ? t.contribute("") : monthly ? t.support(amountValue) : t.contribute(amountValue);

  useEffect(() => { if (open) api.contributionConfig().then(setCfg).catch(() => setCfg({ publishable_key: "" })); }, [open]);
  useEffect(() => { setStage((s) => (s === "success" || s === "error" ? "idle" : s)); setError(""); setResult(null); }, [amount, mode]);
  useEffect(() => {
    if (!open) return;
    if (!document.querySelector(`link[href="${FONT_SRC}"]`)) { const l = document.createElement("link"); l.rel = "stylesheet"; l.href = FONT_SRC; document.head.appendChild(l); }
    const prev = document.activeElement;
    cardRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); (returnFocusRef?.current || prev)?.focus?.(); };
  }, [open, onClose, returnFocusRef]);

  // Mount Stripe card elements once the panel is visible
  const mountCard = useCallback(async (node) => {
    if (!node) { Object.values(elsRef.current).forEach((e) => e?.destroy?.()); elsRef.current = {}; return; }
    if (!cfg?.publishable_key || elsRef.current.number) return;
    const stripe = await stripeFor(cfg.publishable_key);
    if (!stripe) return;
    stripeRef.current = stripe;
    const elements = stripe.elements({ fonts: [{ cssSrc: FONT_SRC }] });
    const number = elements.create("cardNumber", { style: ELEMENT_STYLE, placeholder: t.cardNumber, showIcon: false });
    const expiry = elements.create("cardExpiry", { style: ELEMENT_STYLE, placeholder: t.exp });
    const cvc = elements.create("cardCvc", { style: ELEMENT_STYLE, placeholder: t.cvc });
    number.mount(node.querySelector("[data-el=number]"));
    expiry.mount(node.querySelector("[data-el=expiry]"));
    cvc.mount(node.querySelector("[data-el=cvc]"));
    elsRef.current = { elements, number, expiry, cvc };
    number.on("change", (ev) => { setError(ev.error ? ev.error.message : ""); });
  }, [cfg, t.cardNumber, t.exp, t.cvc]);

  // Apple Pay / Google Pay via Payment Request — shown only when canMakePayment() is true
  useEffect(() => {
    if (!open || !cfg?.publishable_key || !validAmount) { setCanApple(false); return; }
    let cancelled = false;
    (async () => {
      const stripe = await stripeFor(cfg.publishable_key);
      if (!stripe || cancelled) return;
      const pr = stripe.paymentRequest({ country: "DE", currency: "eur", total: { label: `YUO · ${t.contribution}`, amount: Math.round(validAmount * 100) }, requestPayerEmail: true });
      const res = await pr.canMakePayment();
      if (cancelled) return;
      prRef.current = res ? pr : null;
      setCanApple(!!(res && (res.applePay || res.googlePay || res.link)));
      if (res) pr.on("paymentmethod", async (ev) => {
        try {
          const intent = await api.contributionIntent({ mode, amount: validAmount });
          const { error: e1, paymentIntent } = await stripe.confirmCardPayment(intent.client_secret, { payment_method: ev.paymentMethod.id }, { handleActions: false });
          if (e1) { ev.complete("fail"); throw e1; }
          ev.complete("success");
          if (paymentIntent.status === "requires_action") { const { error: e2 } = await stripe.confirmCardPayment(intent.client_secret); if (e2) throw e2; }
          await finish(intent);
        } catch (e) { setStage("error"); setError(e.message || String(e)); }
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cfg, validAmount, mode]);

  const finish = async (intent) => {
    const body = intent.mode === "monthly" ? { subscription_id: intent.ref } : { payment_intent_id: intent.ref };
    const r = await api.contributionConfirm(body);
    setResult(r); setStage(r.paid ? "success" : "error");
    if (!r.paid) setError(`Stripe: ${r.stripe_status}`);
  };

  const payWithCard = async () => {
    if (!validAmount || stage === "processing") return;
    const stripe = stripeRef.current, card = elsRef.current.number;
    if (!stripe || !card) { setError("Payment form is still loading"); return; }
    setStage("processing"); setError("");
    try {
      const intent = await api.contributionIntent({ mode, amount: validAmount });
      intentRef.current = intent;
      const { error: e1 } = await stripe.confirmCardPayment(intent.client_secret, { payment_method: { card } });
      if (e1) throw e1;
      await finish(intent);
    } catch (e) { setStage("error"); setError(e.message || String(e)); }
  };

  if (!open) return null;

  const chip = (v) => {
    const active = amount === v, big = v === 500 || v === 1000;
    return {
      label: v === "custom" ? t.own : `€${v}`,
      bg: active ? (big ? "radial-gradient(120% 200% at 30% 0%, rgba(145,132,217,0.34), rgba(145,132,217,0.10))" : ON) : "radial-gradient(120% 140% at 24% 0%, var(--fg-07), var(--fg-025))",
      color: active ? (big ? "#cfc6f7" : "var(--yuo-ink)") : "var(--fg-6)",
      glow: active ? (big ? "0 0 34px rgba(145,132,217,0.30)" : "0 0 28px rgba(169,183,206,0.22)") : "none",
    };
  };
  const toggle = (on) => ({ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", padding: "10px 22px", borderRadius: 999, border: "none", cursor: "pointer", fontFamily: MONO, background: on ? ON : OFF, color: on ? "var(--yuo-ink)" : "var(--fg-6)", "--g": on ? "0 0 26px rgba(169,183,206,0.22)" : "none" });
  const field = { fontSize: 12.5, padding: "15px 18px", borderRadius: 18, background: "var(--fg-06)", color: "var(--yuo-ink)", minHeight: 47, display: "flex", alignItems: "center" };
  const done = stage === "success";

  return (
    <div data-testid="contribution-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", animation: "yuoVeil .5s ease both", background: "radial-gradient(90% 70% at 50% 0%, rgba(5,6,12,0.72), rgba(3,4,9,0.94))", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", fontFamily: MONO, color: "var(--yuo-ink)", fontWeight: 300 }}>
      <style>{`@keyframes yuoVeil{from{opacity:0}to{opacity:1}}@keyframes yuoEmerge{from{opacity:0;filter:blur(14px);transform:translateY(14px) scale(.985)}to{opacity:1;filter:blur(0);transform:none}}@keyframes yuoBreathe{0%,100%{opacity:.45}50%{opacity:1}}@keyframes yuoSpin{to{transform:rotate(360deg)}}
      .yuo-cb::-webkit-scrollbar{width:0}.App .yuo-cb button,.App .yuo-cb button:hover{animation:none!important;border:none!important;box-shadow:var(--g,none)!important}.App .yuo-cb button:hover:not(:disabled){filter:brightness(1.3)}.App .yuo-cb button:disabled{cursor:default;filter:none;opacity:.6}.App .yuo-cb input,.App .yuo-cb input:focus,.App .yuo-cb input:focus-visible{border:none!important;box-shadow:none!important;outline:none!important;animation:none!important;background:var(--fg-06)!important}.yuo-cb :focus-visible{outline:2px solid #a9b7ce;outline-offset:2px}
      .yuo-cb .yuo-hero{display:grid;grid-template-columns:minmax(0,190px) minmax(0,1fr);gap:30px;align-items:start}.yuo-cb .yuo-two{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.yuo-cb .yuo-chips{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}.yuo-cb .yuo-card-grid{display:grid;grid-template-columns:minmax(0,2fr) minmax(0,1fr) minmax(0,1fr);gap:10px}
      @media (max-width:720px){.yuo-cb{max-height:100vh!important;margin:0!important;padding:26px 20px 30px!important}.yuo-cb .yuo-hero{grid-template-columns:minmax(0,130px) minmax(0,1fr);gap:18px}.yuo-cb .yuo-two,.yuo-cb .yuo-card-grid{grid-template-columns:minmax(0,1fr)}.yuo-cb .yuo-chips{grid-template-columns:repeat(2,minmax(0,1fr))}.yuo-cb .yuo-pay-btn{position:sticky;bottom:0}}`}</style>

      <div ref={cardRef} tabIndex={-1} className="yuo-cb" data-testid="contribution-card" role="dialog" aria-modal="true" aria-label={t.contribution}
        style={{ margin: "auto", width: "min(1020px, 94vw)", maxHeight: "92vh", overflow: "auto", padding: "34px 40px 40px", borderRadius: 38, outline: "none", animation: "yuoEmerge 1s cubic-bezier(.2,.7,.2,1) both", background: "radial-gradient(120% 80% at 18% 0%, rgba(169,183,206,0.10), rgba(169,183,206,0.028) 60%, transparent 100%), rgba(9,11,19,0.72)", boxShadow: "0 0 120px 20px rgba(2,4,8,0.9), inset 0 0 60px rgba(169,183,206,0.05)" }}>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
          <div style={{ ...label, letterSpacing: "0.34em", color: "rgba(169,183,206,0.5)" }}>{t.contribution}</div>
          <button data-testid="contribution-close-btn" onClick={onClose} style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", padding: "7px 16px", borderRadius: 999, border: "none", background: OFF, color: "var(--fg-5)", cursor: "pointer", fontFamily: MONO }}>{t.close}</button>
        </div>

        {/* Creator header */}
        <div className="yuo-hero" style={{ marginTop: 26 }}>
          <div style={{ position: "relative", borderRadius: 28, overflow: "hidden", background: "var(--yuo-bg)", boxShadow: "0 0 50px rgba(2,4,8,0.9), inset 0 0 40px rgba(169,183,206,0.06)" }}>
            <img src="/elena-portrait.jpg" alt="Elena Shamova" style={{ display: "block", width: "100%", aspectRatio: "3/4", objectFit: "cover", objectPosition: "50% 22%", filter: "grayscale(1) contrast(1.05) brightness(.92)", mixBlendMode: "lighten" }} />
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(80% 60% at 50% 110%, rgba(5,6,12,0.85), transparent 70%)" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 400, letterSpacing: "-0.01em", fontFamily: MONO }}>Elena Shamova</h1>
            <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "rgba(169,183,206,0.62)" }}>{t.role}</div>
            <p style={{ margin: 0, maxWidth: 560, fontSize: 13, lineHeight: 1.75, color: "var(--fg-62)", textWrap: "pretty" }}>{t.bio}</p>
          </div>
        </div>

        {/* Mission + sovereignty */}
        <div className="yuo-two" style={{ marginTop: 34 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "24px 26px", borderRadius: 28, background: "radial-gradient(120% 100% at 22% 0%, rgba(169,183,206,0.11), rgba(169,183,206,0.03) 62%, transparent 100%)" }}>
            <div style={{ ...label, color: "rgba(169,183,206,0.55)" }}>{t.mission}</div>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.7, color: "var(--fg-72)" }}>{t.missionText}</p>
            <div style={{ ...label, color: "rgba(169,183,206,0.55)" }}>{t.method}</div>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.7, color: "var(--fg-72)" }}>{t.methodText}</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "24px 26px", borderRadius: 28, background: "radial-gradient(120% 100% at 22% 0%, rgba(145,132,217,0.12), rgba(145,132,217,0.03) 62%, transparent 100%)" }}>
            <div style={{ ...label, color: "rgba(185,175,236,0.62)" }}>{t.safety}</div>
            {t.safetyList.map((s) => (
              <div key={s} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <Stone size={9} color="#b9aeec" shade="#2a2540" glow="0 0 12px rgba(145,132,217,0.5)" style={{ marginTop: 5 }} />
                <span style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--fg-72)" }}>{s}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment layer */}
        <div style={{ marginTop: 34, display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button data-testid="contrib-mode-once" onClick={() => setMode("once")} style={toggle(!monthly)}>{t.once}</button>
            <button data-testid="contrib-mode-monthly" onClick={() => setMode("monthly")} style={toggle(monthly)}>{t.monthly}</button>
          </div>

          <div className="yuo-chips">
            {CHIPS.map((v) => { const c = chip(v); return (
              <button key={v} data-testid={`contrib-chip-${v}`} onClick={() => setAmount(v)} style={{ fontSize: 15, padding: "18px 0", borderRadius: 22, border: "none", cursor: "pointer", fontFamily: MONO, fontWeight: 300, background: c.bg, color: c.color, "--g": c.glow }}>{c.label}</button>
            ); })}
            {amount === "custom" && (
              <input data-testid="contrib-custom-input" autoFocus value={custom} inputMode="decimal" placeholder={t.ownPh}
                onChange={(e) => setCustom(e.target.value.replace(/[^\d.]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); elsRef.current.number?.focus(); } }}
                style={{ gridColumn: "span 3", fontSize: 15, padding: "18px 22px", borderRadius: 22, border: "none", outline: "none", background: "var(--fg-06)", color: "var(--yuo-ink)", fontFamily: MONO }} />
            )}
          </div>

          {hasAmount && (
            <div data-testid="contrib-stripe-panel" style={{ animation: "yuoEmerge .7s cubic-bezier(.2,.7,.2,1) both", display: "flex", flexDirection: "column", gap: 14, padding: "24px 26px", borderRadius: 30, background: "linear-gradient(180deg, var(--fg-055), var(--fg-02))" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <span style={{ ...label, color: "var(--fg-35)" }}>{t.secure}</span>
                <span data-testid="contrib-summary" style={{ fontSize: 13, color: "var(--yuo-ink)" }}>{summary}</span>
              </div>
              {done ? (
                <div data-testid="contrib-success" style={{ padding: "16px 0", borderRadius: 20, textAlign: "center", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", background: ON, color: "var(--yuo-ink)", boxShadow: "0 0 30px rgba(169,183,206,0.24)" }}>
                  {monthly ? t.thanksMonthly : t.thanks}
                </div>
              ) : (
                <>
                  {canApple && (
                    <button data-testid="contrib-apple-pay" onClick={() => prRef.current?.show()} disabled={stage === "processing" || !validAmount}
                      style={{ fontSize: 12, letterSpacing: "0.14em", padding: "16px 0", borderRadius: 20, border: "none", cursor: "pointer", background: "var(--yuo-ink)", color: "var(--yuo-bg)", fontFamily: MONO }}>{t.applePay}</button>
                  )}
                  {canApple && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, ...label, color: "var(--fg-28)" }}>
                      <span style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, var(--fg-18), transparent)" }} />{t.orCard}<span style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, var(--fg-18), transparent)" }} />
                    </div>
                  )}
                  <div ref={mountCard} className="yuo-card-grid" data-testid="contrib-card-fields">
                    <div data-el="number" style={field} />
                    <div data-el="expiry" style={field} />
                    <div data-el="cvc" style={field} />
                  </div>
                  {!cfg?.publishable_key && cfg && (
                    <div style={{ fontSize: 10.5, letterSpacing: "0.1em", color: "var(--fg-45)" }}>Payments are temporarily unavailable — Stripe is not configured.</div>
                  )}
                  {error && <div data-testid="contrib-error" style={{ fontSize: 11.5, lineHeight: 1.6, color: "var(--lit-coral-10)" }}>{error}</div>}
                  <button data-testid="contrib-pay-btn" className="yuo-pay-btn" onClick={payWithCard} disabled={stage === "processing" || !validAmount || !cfg?.publishable_key}
                    style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", padding: "16px 0", borderRadius: 20, border: "none", cursor: "pointer", fontFamily: MONO, background: "radial-gradient(120% 200% at 30% 0%, rgba(169,183,206,0.34), rgba(169,183,206,0.10))", color: "var(--yuo-ink)", "--g": "0 0 30px rgba(169,183,206,0.24)" }}>
                    {stage === "processing" ? t.processing : payLabel}
                  </button>
                </>
              )}
            </div>
          )}

          {/* SIMBIOS zone — always present, last */}
          <div data-testid="contrib-simbios" data-active={simbios ? "1" : "0"} style={{ display: "flex", flexDirection: "column", gap: 14, padding: "26px 28px", borderRadius: 32, opacity: simbios ? 1 : 0.42, transition: "opacity .6s ease", background: simbios ? "radial-gradient(120% 100% at 20% 0%, rgba(145,132,217,0.20), rgba(145,132,217,0.05) 64%, transparent 100%)" : "linear-gradient(180deg, var(--fg-045), var(--fg-018))", boxShadow: simbios ? "0 0 70px rgba(145,132,217,0.20), inset 0 0 50px rgba(145,132,217,0.06)" : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ position: "relative", flex: "none", width: 34, height: 34, borderRadius: "50%", background: "radial-gradient(34% 30% at 32% 24%, #ffffff 0%, #b9aeec 16%, #2a2540 56%, #05060c 100%)", boxShadow: "0 0 26px rgba(145,132,217,0.4), inset -5px -6px 13px rgba(0,0,0,0.85)" }}>
                <span style={{ position: "absolute", inset: 1, borderRadius: "50%", background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.45) 12deg, transparent 28deg, transparent 150deg, rgba(200,190,255,0.4) 162deg, transparent 178deg)", mixBlendMode: "screen", animation: "yuoSpin 26s linear infinite" }} />
              </span>
              <div style={{ fontSize: 12.5, letterSpacing: "0.22em", textTransform: "uppercase", color: simbios ? "#cfc6f7" : "var(--fg-45)" }}>{t.simbiosTitle}</div>
            </div>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.75, color: "var(--fg-72)", textWrap: "pretty" }}>{t.simbiosP1}</p>
            <p style={{ margin: 0, maxWidth: 720, fontSize: 12.5, lineHeight: 1.75, color: "var(--fg-55)", textWrap: "pretty" }}>{t.simbiosP2}</p>
            <div data-testid="contrib-simbios-state" style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: simbios ? "#cfc6f7" : "var(--fg-3)", animation: "yuoBreathe 9s ease-in-out infinite" }}>
              {done && result?.simbios && result?.creator_email ? t.simbiosMail(result.creator_email) : simbios ? t.simbiosOn : t.simbiosOff}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
